<template>
    <div data-test-view="learning-admin-element-edit">
        <div v-if="!element.type">
            <l-learning-admin-element-create-select-type
                :text="$t(`learning.admin.element.create.selectType`)"
                @select="selectType"
                @cancel="goBack"
            />
        </div>

        <div v-else>
            <div v-show="element.usedInPaths && element.usedInPaths.length > 1">
                <p>{{ $t(`learning.admin.element.details.warning.usedInPaths`) }}</p>
                <hr />
            </div>

            <!-- Error -->
            <div v-if="error" id="error">
                <l-text-alert
                    type="danger"
                    :text="$t('learning.admin.element.details.error.edit')"
                />
            </div>
            <!--/Error -->

            <l-learning-admin-element-edit
                :element="element"
                :disable-submit="disableSubmit"
                :show-delete-btn="showDeleteBtn"
                :delete-element="deleteElement"
                :cancel="goBack"
                :submit="updateElement"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import ObjectId from "bson-objectid";

import LTextAlert from "@/components/LTextAlert";

import LLearningAdminElementCreateSelectType from "@/components/LLearningAdminElementCreateSelectType";
import LLearningAdminElementEdit from "@/components/LLearningAdminElementEdit";

export default {
    components: {
        LTextAlert,

        LLearningAdminElementCreateSelectType,
        LLearningAdminElementEdit,
    },
    props: {
        elementId: {
            type: String,
            default() {
                // `id` not passed, generate a new one!
                return ObjectId().toString();
            },
        },
        pathId: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            disableSubmit: false,
            error: false,
            element: {},
        };
    },
    computed: {
        ...mapGetters({
            locale: "users/current/locale",
            key: "learning/storage/key",
            getDetails: "learning/element/details",
        }),
        showDeleteBtn() {
            const { path } = this.$router.currentRoute;

            return (
                path.startsWith("/learning/admin/element") &&
                !path.startsWith("/learning/admin/element/new")
            );
        },
    },
    created() {
        this.element = this.getDetails(this.elementId);
    },
    methods: {
        selectType(type) {
            this.element.type = type;

            // Initialize questions array if not present
            if (this.element.type === "quiz" && !Array.isArray(this.element.questions)) {
                this.element.questions = [];
            }
        },

        goBack() {
            if (
                this.$router.currentRoute.path === "/learning/admin/element/new" &&
                this.element.type
            ) {
                // Go back to the select type screen
                this.element.type = null;
            } else {
                if (this.$router.currentRoute.path.startsWith("/learning/admin/element")) {
                    this.$router.push("/learning/admin/element");
                } else if (this.$router.currentRoute.path.startsWith("/learning/admin/path")) {
                    this.$router.push(`/learning/admin/path/${this.pathId}/edit/steps`);
                } else {
                    this.$router.go(-1);
                }
            }
        },

        async updateElement(element) {
            this.element = element;

            this.error = false;
            this.disableSubmit = true;

            const data = {
                commonId: this.elementId,
                type: this.element.type,
                name: this.element.name,
                description: this.element.description,
                mimetype: this.element.mimetype,
                location: {
                    type: this.element.location.type,
                    url: this.element.location.url,
                    key: this.key || this.element.location.key, // From `learning/storage`
                },
                taggedUsers: (this.element.taggedUsers || []).map(({ _id }) => _id),
                language: this.locale,
                version: this.element.version == null ? 0 : this.element.version + 1,
                questions: this.element.questions,
                tags: [], // TODO
                open: this.element.open,
            };

            this.$store
                .dispatch({
                    type: "learning/element/update",
                    payload: { element: data },
                })
                .then(() => {
                    // Clear storage key
                    this.$store.dispatch({ type: "learning/storage/clear" });

                    // TODO - Split this component
                    if (this.$router.currentRoute.path.startsWith("/learning/admin/path")) {
                        this.$store.dispatch({
                            type: "learning/path/get",
                            payload: { id: this.pathId },
                        });
                    }

                    if (this.$router.currentRoute.path === "/learning/admin/element/new") {
                        this.$router.push("/learning/admin/element");
                    } else {
                        this.goBack();
                    }
                })
                .catch(() => {
                    this.error = true;
                    this.disableSubmit = false;

                    this.$nextTick(() => {
                        document.getElementById("error")?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                            inline: "nearest",
                        });
                    });
                });
        },
        async deleteElement() {
            this.error = false;
            this.disableSubmit = true;

            this.$store
                .dispatch({
                    type: "learning/element/delete",
                    payload: { id: this.elementId },
                })
                .then(() => {
                    // Back to list
                    this.$router.push(`/learning/admin/element`);
                })
                .catch(() => {
                    this.error = true;
                    this.disableSubmit = false;

                    this.$nextTick(() => {
                        document.getElementById("error")?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                            inline: "nearest",
                        });
                    });
                });
        },
    },
};
</script>

<style scoped></style>
